import type { ReactElement } from 'react';

// =============================================================================
// LoadingOverlay
// =============================================================================

export function LoadingOverlay(): ReactElement {
  return (
    <div className="absolute inset-0 bg-theme-surface/80 rounded-xl flex items-center justify-center z-10">
      <div className="flex flex-col items-center gap-2">
        <div className="w-8 h-8 border-2 border-theme-text-tertiary border-t-theme-primary rounded-full animate-spin" />
        <span className="text-sm text-theme-text-tertiary">Loading stats...</span>
      </div>
    </div>
  );
}

// =============================================================================
// ErrorBanner
// =============================================================================

export function ErrorBanner({ error }: { error: string }): ReactElement {
  return (
    <div className="p-3 bg-theme-danger/10 border-b border-theme-danger text-theme-danger text-sm">
      {error}
    </div>
  );
}

// =============================================================================
// KeyboardHints
// =============================================================================

interface KeyboardHintsProps {
  closeLabel?: string;
}

export function KeyboardHints({ closeLabel = 'close' }: KeyboardHintsProps): ReactElement {
  return (
    <div className="px-4 py-2 border-t border-theme-border flex items-center gap-4 text-xs text-theme-text-tertiary">
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 bg-theme-surface-hover rounded text-theme-text-secondary">
          ↑↓
        </kbd>
        navigate
      </span>
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 bg-theme-surface-hover rounded text-theme-text-secondary">
          ↵
        </kbd>
        select
      </span>
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 bg-theme-surface-hover rounded text-theme-text-secondary">
          esc
        </kbd>
        {closeLabel}
      </span>
    </div>
  );
}

// =============================================================================
// ModalShell — wrapper with backdrop + card
// =============================================================================

interface ModalShellProps {
  onClose: () => void;
  children: React.ReactNode;
}

export function ModalShell({ onClose, children }: ModalShellProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        {children}
      </div>
    </div>
  );
}
